import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { MinusIcon, PencilIcon, PlusIcon, TrashIcon } from "lucide-react";
import { useState } from "react";
import { useDragAndDrop } from "react-aria-components";
import {
  Controller,
  type SubmitHandler,
  type UseFormReturn,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { useListData } from "react-stately";
import type { z } from "zod";

import { create as createSchema } from "@blis/shared/schemas/sample-field-spec";
import { trpc } from "@blis/trpc/react";

import { ConfirmPrompt } from "@/components/ConfirmPrompt";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FieldError, Input, Label, TextField } from "@/components/ui/field";
import { Modal } from "@/components/ui/modal";
import {
  Select,
  SelectItem,
  SelectList,
  SelectTrigger,
} from "@/components/ui/select";
import { TableRAC as Table } from "@/components/ui/table-rac";
import { Text } from "@/components/ui/text";
import { mutationResponse } from "@/utils/trpc";
import type { SampleFieldSpecDetail } from "../../types";
import { AdminForm } from "../AdminForm";
import { Section } from "../Section";
import { TabLayout } from "../TabLayout";

type FormValues = z.infer<typeof createSchema>;
type FormReturn = UseFormReturn<
  z.input<typeof createSchema>,
  unknown,
  z.output<typeof createSchema>
>;

type SampleFieldSpecOption = SampleFieldSpecDetail["options"][number];

export function FieldSpecDetailForm({
  field,
}: {
  field?: SampleFieldSpecDetail;
}) {
  const create = trpc.admin.sampleFieldSpec.create.useMutation();
  const update = trpc.admin.sampleFieldSpec.update.useMutation();

  const navigate = useNavigate();

  const methods = useForm({
    defaultValues: {
      name: field?.name ?? "",
      type: field?.type ?? "TEXT",
      options: field?.options ?? [],
      allowFreeText: field?.allowFreeText ?? false,
    },
    resolver: zodResolver(createSchema),
  });

  const { control, handleSubmit, watch } = methods;

  const onSubmit: SubmitHandler<FormValues> = (value) => {
    const { name, type, options, allowFreeText } = value;
    if (field) {
      update.mutate(
        { id: field.id, name, type, allowFreeText },
        mutationResponse({
          errorTitle: "Error updating field",
          successMessage: "Field set updated",
        }),
      );
    } else {
      create.mutate(
        { name, type, options, allowFreeText },
        mutationResponse({
          errorTitle: "Error adding field",
          successMessage: "Field added",
          onSuccess(data) {
            navigate({
              to: "/admin/tubes/fields/$id/edit",
              params: { id: data.id },
            });
          },
        }),
      );
    }
  };

  const type = watch("type");

  return (
    <TabLayout>
      <AdminForm onSubmit={handleSubmit(onSubmit)} className="grow-0">
        <Card className="mb-4">
          <CardContent className="pt-4 flex flex-col gap-4">
            <NameField form={methods} />
            <FieldTypeSelect form={methods} field={field} />
            {(type === "SELECT" || type === "MULTI_SELECT") && (
              <>
                <Controller
                  control={control}
                  name="allowFreeText"
                  render={({ field: ctrlField }) => (
                    <Checkbox
                      name={ctrlField.name}
                      isSelected={ctrlField.value}
                      onChange={(flag) => ctrlField.onChange(!!flag)}
                    >
                      Allow free text
                    </Checkbox>
                  )}
                />
                {!field && <FieldOptionsNew form={methods} />}
              </>
            )}
          </CardContent>
          <CardFooter className="justify-end">
            <Button
              type="submit"
              isPending={create.isPending || update.isPending}
            >
              Save
            </Button>
          </CardFooter>
        </Card>
      </AdminForm>

      {field && (type === "SELECT" || type === "MULTI_SELECT") && (
        <FieldOptionsEdit field={field} />
      )}
    </TabLayout>
  );
}

function NameField({ form }: { form: FormReturn }) {
  return (
    <Controller
      control={form.control}
      name="name"
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          value={field.value ?? ""}
          isInvalid={!!error}
          validationBehavior="aria"
        >
          <Label>Name</Label>
          <Input />
          <FieldError>{error?.message}</FieldError>
        </TextField>
      )}
    />
  );
}

function FieldTypeSelect({
  form,
  field,
}: {
  form: FormReturn;
  field?: SampleFieldSpecDetail;
}) {
  return (
    <Controller
      control={form.control}
      name="type"
      render={({
        field: { ref, ...controllerField },
        fieldState: { error },
      }) => (
        <Select
          {...controllerField}
          selectedKey={controllerField.value}
          onSelectionChange={(value) =>
            controllerField.onChange(value as "TEXT")
          }
          isInvalid={!!error}
          validationBehavior="aria"
          isDisabled={field && field._count.fields > 0}
        >
          <Label>Type</Label>
          <SelectTrigger />
          <SelectList>
            <SelectItem id="TEXT" textValue="Text">
              <Text slot="label">Text</Text>
              <Text slot="description">
                Enter a short answer in a single line
              </Text>
            </SelectItem>
            <SelectItem id="TEXTAREA" textValue="Textarea">
              <Text slot="label">Textarea</Text>
              <Text slot="description">
                Enter a longer answer with multiple lines
              </Text>
            </SelectItem>
            <SelectItem id="NUMBER" textValue="Number">
              <Text slot="label">Number</Text>
              <Text slot="description">
                Enter numbers only (e.g., quantity, measurements)
              </Text>
            </SelectItem>
            <SelectItem id="BOOLEAN" textValue="Boolean">
              <Text slot="label">Boolean</Text>
              <Text slot="description">Simple yes/no checkbox</Text>
            </SelectItem>
            <SelectItem id="DATE" textValue="Date">
              <Text slot="label">Date</Text>
              <Text slot="description">Pick a date from a calendar</Text>
            </SelectItem>
            <SelectItem id="DATETIME" textValue="Date and time">
              <Text slot="label">Date and time</Text>
              <Text slot="description">Pick a date and time</Text>
            </SelectItem>
            <SelectItem id="SELECT" textValue="Select">
              <Text slot="label">Select</Text>
              <Text slot="description">Choose one option from a list</Text>
            </SelectItem>
          </SelectList>
          <FieldError>{error?.message}</FieldError>
        </Select>
      )}
    />
  );
}

function FieldOptionsNew({ form }: { form: FormReturn }) {
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "options",
  });

  return (
    <div className="flex flex-col gap-2">
      <Section.Subtitle>Field options</Section.Subtitle>
      {fields.map((item, index) => (
        <div key={item.id} className="flex items-center gap-2 w-full">
          <Controller
            control={form.control}
            name={`options.${index}.value`}
            render={({ field, fieldState: { error } }) => (
              <TextField
                aria-label={`Option ${index + 1}`}
                autoFocus
                {...field}
                value={field.value ?? ""}
                isInvalid={!!error}
                validationBehavior="aria"
              >
                <Input />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          <Button size="icon" variant="ghost" onPress={() => remove(index)}>
            <MinusIcon data-slot="icon" />
          </Button>
        </div>
      ))}
      <div>
        <Button variant="outline" onPress={() => append({ id: 0, value: "" })}>
          <PlusIcon data-slot="icon" />
          Add option
        </Button>
      </div>
    </div>
  );
}

function FieldOptionsEdit({ field }: { field: SampleFieldSpecDetail }) {
  const [addOptionOpen, setAddOptionOpen] = useState(false);
  const [editOptionOpen, setEditOptionOpen] =
    useState<SampleFieldSpecOption | null>(null);
  const [deleteOptionOpen, setDeleteOptionOpen] =
    useState<SampleFieldSpecOption | null>(null);

  const list = useListData({
    initialItems: field.options || [],
  });

  const updateOrder =
    trpc.admin.sampleFieldSpec.updateOptionsOrder.useMutation();
  const deleteOption = trpc.admin.sampleFieldSpec.deleteOption.useMutation();

  const { dragAndDropHooks } = useDragAndDrop({
    getItems(keys) {
      return [...keys].map((key) => ({
        "text/plain": list.getItem(key)?.value || "",
      }));
    },
    onDragEnd() {
      updateOrder.mutate(
        {
          id: field.id,
          options: list.items.map((item, index) => ({
            id: item.id,
            displayOrder: index,
          })),
        },
        mutationResponse({
          errorTitle: "Error updating options",
          successMessage: "Options updated",
          onError() {
            for (const option of field.options) {
              list.remove(option.id);
              list.append(option);
            }
          },
        }),
      );
    },
    onReorder(e) {
      if (e.target.dropPosition === "before") {
        list.moveBefore(e.target.key, e.keys);
      } else if (e.target.dropPosition === "after") {
        list.moveAfter(e.target.key, e.keys);
      }
    },
  });

  return (
    <>
      <Card className="max-w-3xl">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-1">
              <CardTitle>Field options</CardTitle>
              <CardDescription>
                Add, edit, and remove options for this field
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button onPress={() => setAddOptionOpen(true)} variant="outline">
                <PlusIcon data-slot="icon" />
                Add option
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table aria-label="Field options" dragAndDropHooks={dragAndDropHooks}>
            <Table.Header>
              <Table.Row>
                <Table.Column isRowHeader>Option</Table.Column>
                <Table.Column />
              </Table.Row>
            </Table.Header>
            <Table.Body items={list.items}>
              {(item) => (
                <Table.Row key={item.id}>
                  <Table.Cell>{item.value}</Table.Cell>
                  <Table.Cell>
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="icon"
                        variant="ghost"
                        onPress={() => setEditOptionOpen(item)}
                      >
                        <PencilIcon data-slot="icon" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onPress={() => setDeleteOptionOpen(item)}
                      >
                        <TrashIcon data-slot="icon" />
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </CardContent>
      </Card>

      <ConfirmPrompt
        danger
        open={!!deleteOptionOpen}
        onOpenChange={() => setDeleteOptionOpen(null)}
        onConfirm={() => {
          if (deleteOptionOpen) {
            deleteOption.mutate(
              { id: deleteOptionOpen.id },
              mutationResponse({
                errorTitle: "Error removing option",
                successMessage: "Option removed",
                onSuccess() {
                  list.remove(deleteOptionOpen.id);
                  setDeleteOptionOpen(null);
                },
              }),
            );
          }
        }}
        loading={deleteOption.isPending}
        title="Remove option"
        message={`Are you sure you want to remove option: ${deleteOptionOpen?.value}?`}
      />
      <Modal
        isOpen={!!editOptionOpen}
        onOpenChange={() => setEditOptionOpen(null)}
      >
        <NewOrEditOption
          option={editOptionOpen}
          field={field}
          onUpdate={(option) => {
            list.update(option.id, option);
            setEditOptionOpen(null);
          }}
          onCancel={() => setEditOptionOpen(null)}
        />
      </Modal>

      <Modal
        isOpen={addOptionOpen}
        onOpenChange={() => setAddOptionOpen(false)}
      >
        <NewOrEditOption
          option={null}
          field={field}
          onCreate={(option) => {
            list.append(option);
            setAddOptionOpen(false);
          }}
          onCancel={() => setAddOptionOpen(false)}
        />
      </Modal>
    </>
  );
}

function NewOrEditOption({
  option,
  field,
  onCreate,
  onUpdate,
  onCancel,
}: {
  option?: SampleFieldSpecOption | null;
  field: SampleFieldSpecDetail;
  onCreate?: (option: SampleFieldSpecOption) => void;
  onUpdate?: (option: SampleFieldSpecOption) => void;
  onCancel?: () => void;
}) {
  const update = trpc.admin.sampleFieldSpec.updateOption.useMutation(
    mutationResponse({
      errorTitle: "Error updating option",
      successMessage: "Option updated",
      onSuccess(data) {
        if (onUpdate) {
          onUpdate(data);
        }
      },
    }),
  );
  const create = trpc.admin.sampleFieldSpec.createOption.useMutation(
    mutationResponse({
      errorTitle: "Error adding option",
      successMessage: "Option added",
      onSuccess(data) {
        if (onCreate) {
          onCreate(data);
        }
      },
    }),
  );

  const [value, setValue] = useState(option?.value ?? "");

  const handleFormSubmit = () => {
    if (option) {
      update.mutate({ id: option.id, value });
    } else {
      create.mutate({ fieldSpecId: field.id, value });
    }
  };

  return (
    <Dialog>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleFormSubmit();
        }}
      >
        <DialogHeader>
          <DialogTitle>{option ? "Edit option" : "Add option"}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <TextField value={value} onChange={setValue}>
            <Label>Value</Label>
            <Input />
          </TextField>
        </DialogBody>
        <DialogFooter>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onPress={onCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              isPending={create.isPending || update.isPending}
            >
              Save
            </Button>
          </div>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
